import React, { useMemo } from 'react';
import { StyleSheet, View, ActivityIndicator } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useTheme } from '@/context/ThemeContext';

const createStyles = (colors) => StyleSheet.create({
  container: {
    width: '100%',
    padding: 12,
    borderRadius: 4,
    marginBottom: 6,
  },
  successContainer: {
    backgroundColor: `rgba(34, 197, 94, 0.1)`,
    borderWidth: 1,
    borderColor: colors.color_success,
  },
  errorContainer: {
    backgroundColor: `rgba(255, 59, 48, 0.1)`,
    borderWidth: 1,
    borderColor: colors.color_destructive,
  },
  loadingContainer: {
    backgroundColor: colors.solid_color_tertiary,
  },
  emptyContainer: {
    backgroundColor: colors.solid_color_tertiary,
  },
  upgradeContainer: {
    backgroundColor: `rgba(147, 51, 234, 0.1)`,
    borderWidth: 1,
    borderColor: colors.accent_color_primary,
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  iconContainer: {
    marginRight: 8,
  },
  loadingIconContainer: {
    marginTop: -2,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    marginBottom: 4,
  },
  text: {
    textAlign: 'left',
  },
  successText: {
    color: colors.color_success,
  },
  errorText: {
    color: colors.color_destructive,
  },
  loadingText: {
    color: colors.text_color_primary,
  },
  emptyText: {
    color: colors.text_color_secondary,
  },
  upgradeText: {
    color: colors.accent_color_primary,
  },
  actionContainer: {
    marginTop: 12,
    flexDirection: 'row',
  },
  actionButton: {
    paddingHorizontal: 15,
  },
});

const StatusContainer = ({ 
  type = 'error',
  title,
  message,
  style,
  hideIcon = false,
  actionText,
  onAction,
  icon,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const getContainerStyle = useMemo(() => {
    switch (type) {
      case 'success':
        return styles.successContainer;
      case 'error':
        return styles.errorContainer;
      case 'loading':
        return styles.loadingContainer;
      case 'empty':
        return styles.emptyContainer;
      case 'upgrade':
        return styles.upgradeContainer;
      default:
        return styles.errorContainer;
    }
  }, [type, styles]);

  const getTextStyle = useMemo(() => {
    switch (type) {
      case 'success':
        return styles.successText;
      case 'error':
        return styles.errorText;
      case 'loading':
        return styles.loadingText;
      case 'empty':
        return styles.emptyText;
      case 'upgrade':
        return styles.upgradeText;
      default:
        return styles.errorText;
    }
  }, [type, styles]);

  const getIconContainerStyle = useMemo(() => {
    const baseStyle = [styles.iconContainer];
    if (type === 'loading') {
      baseStyle.push(styles.loadingIconContainer);
    }
    return baseStyle;
  }, [type, styles]);

  const getActionButtonStyle = useMemo(() => {
    return [styles.actionButton];
  }, [styles]);

  const getActionButtonTextStyle = useMemo(() => {
    switch (type) {
      case 'success':
        return styles.successActionButtonText;
      case 'error':
        return styles.errorActionButtonText;
      default:
        return undefined;
    }
  }, [type, styles]);

  const renderIcon = () => {
    if (hideIcon) return null;

    switch (type) {
      case 'success':
        return <Icon name="circle-check" size={16} color={colors.color_success} />;
      case 'error':
        return <Icon name="circle-xmark" size={16} color={colors.color_destructive} />;
      case 'loading':
        return <ActivityIndicator size="small" color={colors.text_color_primary} />;
      case 'empty':
        return <Icon name="circle-info" size={16} color={colors.text_color_secondary} />;
      case 'upgrade':
        return <Icon name={icon || "sparkles"} size={16} color={colors.text_color_primary} />;
      default:
        return null;
    }
  };

  return (
    <View style={[
      styles.container, 
      getContainerStyle,
      style
    ]}>
      <View style={styles.contentContainer}>
        {!hideIcon && (
          <View style={getIconContainerStyle}>
            {renderIcon()}
          </View>
        )}
        <View style={styles.textContainer}>
          {title && (
            <Text 
              size='n'
              weight='medium'
              style={[
                styles.text,
                styles.title,
                getTextStyle
              ]}
            >
              {title}
            </Text>
          )}
          {message && (
            <Text 
              size='n' 
              style={[
                styles.text,
                getTextStyle
              ]}
            >
              {message}
            </Text>
          )}
          {actionText && onAction && (
            <View style={styles.actionContainer}>
              <Button
                text={actionText}
                onPress={onAction}
                size="small"
                variant={type === 'error' ? 'danger' : type === 'success' ? 'success' : 'secondary'}
                isHollow
                style={getActionButtonStyle}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

StatusContainer.propTypes = {
  type: PropTypes.oneOf(['error', 'success', 'loading', 'empty', 'upgrade']),
  title: PropTypes.string,
  message: PropTypes.string,
  style: PropTypes.object,
  hideIcon: PropTypes.bool,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  icon: PropTypes.string,
};

export default React.memo(StatusContainer); 